import { useEffect, useRef } from 'react'

import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'

import { useCommonTrackingProperties } from '~publish/hooks/useCommonTrackingProperties'

import type { TakeawayItem } from '../components/Takeaways/types'

interface UseTrackTakeawaysViewedArgs {
  channelId: string
  organizationId: string
  /** Whether takeaways are still loading; the event waits until this is false. */
  loading: boolean
  /** The final rendered takeaway list (post-merge, post-truncation). */
  visibleTakeaways: ReadonlyArray<TakeawayItem>
}

/**
 * Emits a single `Takeaways Viewed` event per channel Insights page view, once
 * takeaways have finished loading and at least one card is rendered. The event
 * describes the final rendered list, so callers must pass the same array given
 * to `<Takeaways />` to keep the tracked list from drifting from the UI.
 *
 * The guard is keyed by `channelId` rather than a boolean: the Insights page
 * stays mounted when the user switches channels (only the route param changes),
 * so a once-per-mount guard would never re-fire for the next channel. Keying on
 * `channelId` fires exactly once per channel and re-arms on switch.
 */
export function useTrackTakeawaysViewed({
  channelId,
  organizationId,
  loading,
  visibleTakeaways,
}: UseTrackTakeawaysViewedArgs): void {
  const commonTrackingProperties = useCommonTrackingProperties()
  const trackedChannelIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (loading) return
    if (visibleTakeaways.length === 0) return
    if (trackedChannelIdRef.current === channelId) return

    trackedChannelIdRef.current = channelId

    BufferTracker.takeawaysViewed({
      ...commonTrackingProperties,
      clientName: 'publishWeb',
      organizationId: organizationId || commonTrackingProperties.organizationId,
      takeawayCards: visibleTakeaways.map((takeaway, position) => ({
        id: takeaway.id ?? null,
        category: takeaway.category,
        position,
      })),
      takeawayCount: visibleTakeaways.length,
    })
  }, [
    loading,
    visibleTakeaways,
    channelId,
    organizationId,
    commonTrackingProperties,
  ])
}
